import React, { useCallback, useMemo, useContext } from "react";
import { Button, IconButton } from "@material-tailwind/react";
import { ArrowRightIcon, ArrowLeftIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from "@heroicons/react/24/outline";
import recipes from "../API/recipes";
import { RecepiesContext } from "../context/RepiesContext";
import { focusSearch } from "../utils";

const PAGE_SIZE = 20;

export function DefaultPagination({
  number = PAGE_SIZE,
  offset = 0,
  totalResults = 0,
  cuisine,
  diet,
  intolerances = [],
}) {
  const { setRecepies } = useContext(RecepiesContext);

  const { currentPage, totalPages } = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(totalResults / PAGE_SIZE));
    const currentPage = Math.min(totalPages, Math.floor(offset / PAGE_SIZE) + 1);
    return { currentPage, totalPages };
  }, [offset, totalResults]);

  const goToPage = useCallback(
    (page) => {
      const safePage = Math.max(1, Math.min(totalPages, page));
      const newOffset = (safePage - 1) * PAGE_SIZE;
      recipes
        .getRecepies({ offset: newOffset, cuisine, diet, intolerances })
        .then((res) => {
          setRecepies({ ...res.data, cuisine, diet, intolerances });
          focusSearch();
        });
    },
    [cuisine, diet, intolerances, setRecepies, totalPages]
  );

  const windowPages = useMemo(() => {
    // Show: first, last, current±1 (or ±2 if near edges) with ellipses
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    // if near edges, add more neighbors
    if (currentPage <= 3) pages.add(3);
    if (currentPage >= totalPages - 2) pages.add(totalPages - 2);

    const sorted = [...pages].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);

    // Insert ellipses markers as 0
    const out = [];
    for (let i = 0; i < sorted.length; i++) {
      out.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) out.push(0);
    }
    return out;
  }, [currentPage, totalPages]);

  const disabledPrev = currentPage <= 1;
  const disabledNext = currentPage >= totalPages;

  if (totalPages <= 1) return null;

  return (
    <nav className="flex flex-wrap items-center gap-2 text-sm" aria-label="Pagination">
      <Button
        variant="text"
        className="flex items-center gap-1"
        onClick={() => goToPage(1)}
        disabled={disabledPrev}
      >
        <ChevronDoubleLeftIcon className="h-4 w-4" /> First
      </Button>

      <Button
        variant="text"
        className="flex items-center gap-1"
        onClick={() => goToPage(currentPage - 1)}
        disabled={disabledPrev}
      >
        <ArrowLeftIcon className="h-4 w-4" /> Prev
      </Button>

      <div className="flex items-center gap-1">
        {windowPages.map((p, idx) =>
          p === 0 ? (
            <span key={`e-${idx}`} className="px-2 text-gray-500">…</span>
          ) : (
            <IconButton
              key={p}
              variant={p === currentPage ? "filled" : "text"}
              color={p === currentPage ? "blue" : "gray"}
              onClick={() => goToPage(p)}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </IconButton>
          )
        )}
      </div>

      <Button
        variant="text"
        className="flex items-center gap-1"
        onClick={() => goToPage(currentPage + 1)}
        disabled={disabledNext}
      >
        Next <ArrowRightIcon className="h-4 w-4" />
      </Button>

      <Button
        variant="text"
        className="flex items-center gap-1"
        onClick={() => goToPage(totalPages)}
        disabled={disabledNext}
      >
        Last <ChevronDoubleRightIcon className="h-4 w-4" />
      </Button>
    </nav>
  );
}

export default DefaultPagination;
